import { ResourceConfig } from "../types";

export const resourceConfigs: Record<string, ResourceConfig> = {
  customers: {
    title: "Customers",
    resource: "customers",
    description: "Manage families, customers, and their contactable business details.",
    columns: [
      { key: "customer_code", label: "Code" },
      { key: "display_name", label: "Name" },
      { key: "customer_type", label: "Type" },
      { key: "primary_phone", label: "Phone" },
      { key: "primary_email", label: "Email" },
      { key: "city", label: "City" }
    ],
    formFields: [
      { name: "customer_code", label: "Customer Code", generatedOnCreate: true, readOnly: true, helperText: "Generated automatically after save." },
      {
        name: "customer_type",
        label: "Customer Type",
        type: "select",
        required: true,
        options: [
          { label: "Family", value: "family" },
          { label: "Individual", value: "individual" },
          { label: "Corporate", value: "corporate" }
        ]
      },
      { name: "display_name", label: "Display Name", required: true },
      { name: "family_name", label: "Family Name" },
      { name: "primary_phone", label: "Primary Phone", required: true, maxLength: 10, pattern: "^\\d{10}$", patternMessage: "Enter exactly 10 digits" },
      { name: "whatsapp_phone", label: "WhatsApp Phone", maxLength: 10, pattern: "^\\d{10}$", patternMessage: "Enter exactly 10 digits" },
      { name: "primary_email", label: "Primary Email", type: "email" },
      { name: "city", label: "City", required: true },
      { name: "state", label: "State", required: true },
      { name: "country", label: "Country", required: true },
      { name: "notes", label: "Notes", type: "textarea" },
      { name: "is_active", label: "Active", type: "checkbox" }
    ]
  },
  events: {
    title: "Events",
    resource: "events",
    description: "Manage event metadata used to prepare quotations and execution plans.",
    columns: [
      { key: "event_code", label: "Code" },
      { key: "title", label: "Title" },
      { key: "host_family_name", label: "Family" },
      { key: "groom_name", label: "Name" },
      { key: "venue_name", label: "Venue" },
      { key: "event_start_at", label: "Event Start" }
    ],
    formFields: [
      { name: "event_code", label: "Event Code", generatedOnCreate: true, readOnly: true, helperText: "Generated automatically after save." },
      { name: "customer_id", label: "Customer", type: "select", required: true, optionsResource: "customers", optionLabelKey: "display_name", optionValueKey: "id" },
      { name: "event_type_id", label: "Event Type", type: "select", required: true, optionsResource: "event-types", optionLabelKey: "type_name", optionValueKey: "id" },
      { name: "title", label: "Title", required: true },
      { name: "occasion_label", label: "Occasion" },
      { name: "host_family_name", label: "Host Family" },
      { name: "groom_name", label: "Groom" },
      { name: "bride_name", label: "Bride" },
      { name: "theme_name", label: "Theme" },
      { name: "decor_type_label", label: "Decor Type" },
      { name: "venue_name", label: "Venue", required: true },
      { name: "venue_city", label: "Venue City", required: true },
      { name: "venue_state", label: "Venue State", required: true },
      { name: "guest_count", label: "Guest Count", type: "number" },
      { name: "event_start_at", label: "Event Start", type: "date" },
      { name: "event_end_at", label: "Event End", type: "date" },
      { name: "customer_notes", label: "Customer Notes", type: "textarea" },
      { name: "internal_notes", label: "Internal Notes", type: "textarea" }
    ]
  },
  templates: {
    title: "Quotation Templates",
    resource: "quotation-templates",
    description: "Manage reusable quotation themes, layout settings, and print configuration.",
    columns: [
      { key: "template_code", label: "Code" },
      { key: "template_name", label: "Template Name" },
      { key: "status", label: "Status" },
      { key: "base_layout_key", label: "Layout" },
      { key: "default_currency_code", label: "Currency" }
    ],
    formFields: [
      { name: "organization_profile_id", label: "Organization", type: "select", required: true, optionsResource: "organization-profiles", optionLabelKey: "business_name", optionValueKey: "id" },
      { name: "event_type_id", label: "Event Type", type: "select", optionsResource: "event-types", optionLabelKey: "type_name", optionValueKey: "id" },
      { name: "template_code", label: "Template Code", required: true },
      { name: "template_name", label: "Template Name", required: true },
      { name: "description", label: "Description", type: "textarea" },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
          { label: "Archived", value: "archived" }
        ]
      },
      { name: "base_layout_key", label: "Layout Key", required: true },
      { name: "default_currency_code", label: "Currency", required: true },
      { name: "color_tokens", label: "Color Tokens JSON", type: "json" },
      { name: "header_config", label: "Header Config JSON", type: "json" },
      { name: "footer_config", label: "Footer Config JSON", type: "json" },
      { name: "print_config", label: "Print Config JSON", type: "json" }
    ]
  },
  catalog: {
    title: "Item Master",
    resource: "service-catalog-items",
    description: "Manage reusable master items used inside quotation sections and templates.",
    columns: [
      { key: "category_id", label: "Category" },
      { key: "item_code", label: "Item Code" },
      { key: "item_name", label: "Item Name" },
      { key: "default_unit", label: "Unit" },
      { key: "default_unit_rate", label: "Default Rate" },
      { key: "pricing_mode", label: "Pricing Mode" }
    ],
    listFilters: [
      { name: "category_id", label: "Category", type: "select", optionsResource: "service-categories", optionLabelKey: "category_name", optionValueKey: "id" }
    ],
    formFields: [
      { name: "category_id", label: "Category", type: "select", required: true, optionsResource: "service-categories", optionLabelKey: "category_name", optionValueKey: "id" },
      { name: "item_code", label: "Item Code", generatedOnCreate: true, readOnly: true, helperText: "Generated automatically after save." },
      { name: "item_name", label: "Item Name", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "default_unit", label: "Default Unit" },
      { name: "default_dimension_text", label: "Dimension Size", required: true },
      { name: "default_unit_rate", label: "Default Unit Rate", type: "number", required: true, min: 0 },
      {
        name: "pricing_mode",
        label: "Pricing Mode",
        type: "select",
        required: true,
        options: [
          { label: "Manual", value: "manual" },
          { label: "Quantity x Rate", value: "quantity_x_rate" }
        ]
      },
      { name: "is_active", label: "Active", type: "checkbox" }
    ]
  },
  categories: {
    title: "Service Categories",
    resource: "service-categories",
    description: "Manage top-level service categories used in quotation templates and quote sections.",
    columns: [
      { key: "event_type_id", label: "Event Type" },
      { key: "category_code", label: "Code" },
      { key: "category_name", label: "Category Name" },
      { key: "default_display_order", label: "Display Order" },
      { key: "is_active", label: "Active" }
    ],
    formFields: [
      { name: "event_type_id", label: "Event Type", type: "select", required: true, optionsResource: "event-types", optionLabelKey: "type_name", optionValueKey: "id" },
      { name: "category_code", label: "Category Code", required: true },
      { name: "category_name", label: "Category Name", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "default_display_order", label: "Display Order", type: "number", required: true, min: 0 },
      { name: "is_active", label: "Active", type: "checkbox" }
    ]
  },
  "custom-questions": {
    title: "Custom Questions",
    resource: "custom-questions",
    description: "Manage standalone questions for customer discovery, planning, and print-ready checklists.",
    columns: [
      { key: "display_order", label: "Order" },
      { key: "question_text", label: "Question" },
      { key: "answer_type", label: "Answer Type" },
      { key: "is_required", label: "Required" },
      { key: "is_active", label: "Active" }
    ],
    formFields: [
      { name: "question_text", label: "Question", type: "textarea", required: true },
      {
        name: "answer_type",
        label: "Answer Type",
        type: "select",
        required: true,
        options: [
          { label: "Short Text", value: "text" },
          { label: "Long Text", value: "textarea" },
          { label: "Number", value: "number" },
          { label: "Date", value: "date" },
          { label: "Yes / No", value: "yes_no" }
        ]
      },
      { name: "placeholder_text", label: "Placeholder Text" },
      { name: "help_text", label: "Help Text", type: "textarea" },
      { name: "display_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_required", label: "Required", type: "checkbox" },
      { name: "is_active", label: "Active", type: "checkbox" }
    ]
  },
  users: {
    title: "Admin Users",
    resource: "users",
    description: "Manage internal and customer users who can access quotations or the admin system.",
    columns: [
      { key: "display_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "user_type", label: "User Type" },
      { key: "status", label: "Status" },
      { key: "phone", label: "Phone" }
    ],
    formFields: [
      { name: "customer_id", label: "Customer", type: "select", optionsResource: "customers", optionLabelKey: "display_name", optionValueKey: "id" },
      {
        name: "user_type",
        label: "User Type",
        type: "select",
        required: true,
        options: [
          { label: "Internal", value: "internal" },
          { label: "Customer", value: "customer" }
        ]
      },
      { name: "first_name", label: "First Name", required: true },
      { name: "last_name", label: "Last Name", required: true },
      { name: "display_name", label: "Display Name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone", required: true, maxLength: 10, pattern: "^\\d{10}$", patternMessage: "Enter exactly 10 digits" },
      { name: "password", label: "Password", requiredOnCreate: true, minLength: 6, helperText: "Enter a new password when creating or changing login access." },
      {
        name: "status",
        label: "Status",
        type: "select",
        required: true,
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
          { label: "Blocked", value: "blocked" },
          { label: "Pending Verification", value: "pending_verification" }
        ]
      }
    ]
  }
};

export const resourcePathMap: Record<string, string> = {
  customers: "customers",
  events: "events",
  templates: "templates",
  catalog: "catalog",
  categories: "categories",
  "custom-questions": "custom-questions",
  users: "users"
};
